import type { ComicCard, ComicSource, Page, SearchQuery, SourceAuth } from '@/sources/types';
import { RateLimited } from '../../lib/errors.ts';

const EH_AGGREGATE_SLOT = 'ehentai-family';

export interface AggregateSourceResult {
  source: ComicSource;
  items: ComicCard[];
  error?: unknown;
  loading: boolean;
}

export interface RunAggregateSourceSearchOptions {
  sources: readonly ComicSource[];
  query: SearchQuery;
  loadAuth?: (source: ComicSource) => Promise<SourceAuth | undefined>;
  fetchPage: (
    source: ComicSource,
    query: SearchQuery,
    auth: SourceAuth | undefined,
  ) => Promise<Page<ComicCard>>;
  onResult: (result: AggregateSourceResult) => void;
  signal?: AbortSignal;
}

/**
 * Cross-source search can only safely share common query fields. `extra`
 * belongs to one source's filter schema, so passing it to every source can
 * produce incompatible requests.
 */
export function aggregateCompatibleQuery(query: SearchQuery): SearchQuery {
  const next: SearchQuery = { ...query };
  delete next.extra;
  return next;
}

export function aggregateSourceSlot(source: ComicSource): string {
  return source.id === 'ehentai' || source.id === 'exhentai' ? EH_AGGREGATE_SLOT : source.id;
}

export function aggregateDisplaySources(sources: readonly ComicSource[]): ComicSource[] {
  return aggregateSourceGroups(sources).map((group) => group[0]).filter((source) => source !== undefined);
}

/**
 * Fan out to every source and report each result as soon as that source
 * settles. This keeps fast sources usable when one source is rate-limited,
 * behind Cloudflare, or otherwise slow.
 */
export async function runAggregateSourceSearch({
  sources,
  query,
  loadAuth,
  fetchPage,
  onResult,
  signal,
}: RunAggregateSourceSearchOptions): Promise<void> {
  const aggregateQuery = aggregateCompatibleQuery(query);

  await Promise.allSettled(
    aggregateSourceGroups(sources).map(async (group) => {
      const failures: AggregateSourceResult[] = [];
      for (const source of group) {
        try {
          const auth = await loadAuth?.(source);
          if (signal?.aborted) return;
          const page = await fetchPage(source, aggregateQuery, auth);
          if (!signal?.aborted) {
            onResult({ source, items: page.items, loading: false });
          }
          return;
        } catch (error) {
          failures.push({ source, items: [], loading: false, error });
        }
      }
      if (!signal?.aborted) {
        const actionable = failures.find((failure) => failure.error instanceof RateLimited);
        const result = actionable ?? failures[0];
        if (result) onResult(result);
      }
    }),
  );
}

function aggregateSourceGroups(sources: readonly ComicSource[]): ComicSource[][] {
  const ehSources = sources
    .filter((source) => source.id === 'ehentai' || source.id === 'exhentai')
    .sort((left, right) => Number(right.id === 'exhentai') - Number(left.id === 'exhentai'));
  const groups: ComicSource[][] = [];
  let addedEhSources = false;

  for (const source of sources) {
    if (aggregateSourceSlot(source) === EH_AGGREGATE_SLOT) {
      if (!addedEhSources && ehSources.length > 0) {
        groups.push(ehSources);
        addedEhSources = true;
      }
      continue;
    }
    groups.push([source]);
  }
  return groups;
}
